import EventDispatcher from "../../../../../shared/lib/html5-software-library/src/ranj/util/EventDispatcher";
import { getURLParameter } from "../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil";
import GameEvent from "../../../../../shared/src/ranj/app/core/GameEvent";
import ProgressValue from "../../../../../shared/src/ranj/equalture/platform/ProgressValue";
import GameConfig from "./GameConfig";
import GameStatus from "./GameStatus";
import PuzzleModel from "./PuzzleModel";
import ScoreCalculator from "./ScoreCalculator";
import TrackingController from "./TrackingController";

export default class GameModel extends EventDispatcher {

    /**
     * @param {GameConfig} gameConfig 
     * @param {TrackingController} trackingController 
     */
    constructor(dispatcher, gameConfig, trackingController) {
        super();
        this._contextDispatcher = dispatcher;
        this._config = gameConfig;
        this._trackingController = trackingController;
    }

    dispatchPlatformProgressEvent(progressValue) {
        this._trackingController.trackProgress(progressValue, this.elapsedTime);
    }

    initialize() {
        this._scoreCalculator = new ScoreCalculator(this._config);
        this.reset();
    }

    reset() {
        this._puzzleIndex = -1;
        this._elapsedTime = 0;
        this._rawScore = 0;
        const debugTime = parseInt(getURLParameter("time"));
        this._availableTime = (!isNaN(debugTime) ? debugTime : this._config.time) * 1000;
    }

    set restoredFeedbackPending(bool) {
        this._restoredFeedbackPending = bool;
    }
    get restoredFeedbackPending() {
        return this._restoredFeedbackPending;
    }

    start() {
        this.dispatchPlatformProgressEvent(ProgressValue.STARTED);
        this.status = GameStatus.RUNNING;
    }

    end() {
        this.status = GameStatus.END;
        this.dispatchPlatformProgressEvent(ProgressValue.COMPLETED);
    }

    timeout() {
        this.status = GameStatus.TIMEOUT;
        this.dispatchPlatformProgressEvent(ProgressValue.COMPLETED);
    }

    step(dt) {
        if (this.status != GameStatus.RUNNING) {
            return;
        }
        this.elapsedTime += dt;
        if (this.remainingTime <= 0) {
            this.timeout();
        }
    }

    set elapsedTime(value) {
        this._elapsedTime = value;
        this.dispatchEvent(GameModel.ELAPSED_TIME, this._elapsedTime);
    }
    get elapsedTime() {
        return this._elapsedTime;
    }
    get remainingTime() {
        return this._availableTime - this._elapsedTime;
    }

    /**
     * @type {Number} Range [0..1]. Average accuracy score over all puzzles.
     */
    get score() {
        if (this.puzzleIndex <= 0) {
            return 0;
        }
        return this._rawScore / this.puzzleIndex;
    }

    set status(value) {
        this._status = value;
        this.dispatchEvent(GameModel.STATUS, value);
    }
    get status() {
        return this._status;
    }


    doNextPuzzle() {
        if (!this.hasNextPuzzle()) {
            return false;
        } else {
            this.initPuzzle(this._puzzleIndex + 1);
            return true;
        }
    }

    initTutorialPuzzle() {
        this._puzzleModel = new PuzzleModel(this._config.getValue("tutorial.puzzle"), this._scoreCalculator);
        this.dispatchEvent(GameModel.PUZZLE, this._puzzleModel);
    }

    /**
     * 
     * @param {Number} index 
     * @param {Object} puzzleState - generic object containing savegame data for puzzle
     */
    initPuzzle(index, puzzleState = null) {
        this._destroyPuzzle();
        this._puzzleIndex = index;
        this._puzzleModel = new PuzzleModel(this._config.getPuzzle(index), this._scoreCalculator);
        this._addPuzzleEventListeners();
        if (puzzleState) {
            this._puzzleModel.setFromObject(puzzleState);
        }
        this.dispatchEvent(GameModel.PUZZLE, this._puzzleModel);
    }
    
    _destroyPuzzle() {
        if (this._puzzleModel) {
            this._puzzleModel.removeEventListener(PuzzleModel.MOVE, this._handlePuzzleMove);
            this._puzzleModel = null;
        }
    }

    _addPuzzleEventListeners() {
        this._puzzleModel.addEventListener(PuzzleModel.MOVE, this._handlePuzzleMove);
    }

    //UNUSED
    _handlePuzzleMove(e) {
        //TODO: track
        //const { type, count, actor } = e.data;
        //const totalCount = this._puzzleModel.totalMoveCount;
        //this._trackingController.trackMove(this._puzzleIndex, totalCount, type, this.elapsedTime);
        this._propagateEvent(e);
    }

    submitPuzzle() {
        this._rawScore += this._puzzleModel.score;
        this.dispatchEvent(GameModel.SCORE, this.score);
    }

    hasNextPuzzle() {
        return this._puzzleIndex < this._config.puzzleCount - 1;
    }

    resetPuzzle() {
        this._puzzleModel.reset();
    }

    _propagateEvent(e) {
        this.dispatchEvent(e.type, e.data);
    }

    get puzzle() {
        return this._puzzleModel;
    }

    get puzzleIndex() {
        return this._puzzleIndex;
    }

};


GameModel.STATUS = "GameModel.STATUS";
GameModel.PUZZLE = "GameModel.PUZZLE";
GameModel.SCORE = "GameModel.SCORE";
GameModel.ELAPSED_TIME = "GameModel.TIME";